import { useMemo } from "react";
import { fromMM } from "../Utils/frameUtils";
import { buildPanel } from "../Utils/geometryUtils";
import FSSProperties from "./Properties/FSSProperties";
import { Edges } from "@react-three/drei";
import { useStore } from "../Store/zustandStore";
import * as THREE from "three";

export default function FSS({ frame }) {
    const [frameThickness] = useStore((state) => [fromMM(state.frameThickness)]);

    const { Fire } = frame;


    const frameLength = fromMM(frame.length);
    const frameHeight = fromMM(frame.height);
    const frameWidth = fromMM(frame.width);


    const boxLength = fromMM(Fire?.boxLength || FSSProperties.boxLength);
    const boxHeight = fromMM(Fire?.boxHeight || FSSProperties.boxHeight);
    const boxWidth = fromMM(Fire?.boxWidth || FSSProperties.boxWidth);

    const outerPipeRadius = 0.025;
    const innerPipeRadius = 0.015;
    const gap = 0.01;
    const outerPipeSegment1Length = frameWidth - boxWidth - frameThickness;
    const outerPipeSegment2Length = frameHeight - boxHeight - fromMM(150);
    const outerPipeSegment3Length = outerPipeSegment1Length;
    const innerPipeSegment1Length = outerPipeSegment1Length - 0.05;
    const innerPipeSegment2Length = outerPipeSegment2Length - 0.05;
    const innerPipeSegment3Length = innerPipeSegment1Length;
    const innerOffset = outerPipeRadius + innerPipeRadius + gap;


    const sprayRadius = 0.1;
    const sprayLength = 0.2;


    const topOffset = fromMM(60);
    const boxPosition = [0, frameHeight / 2 - boxHeight / 2 - topOffset, -frameWidth / 2 - boxWidth / 2 - frameThickness];


    const outerPipeStart = [boxPosition[0], boxPosition[1] + boxHeight / 2.5, boxPosition[2]];
    const outerPipeSegment1End = [outerPipeStart[0], outerPipeStart[1], outerPipeStart[2] + outerPipeSegment1Length];
    const outerPipeSegment2End = [outerPipeSegment1End[0], outerPipeSegment1End[1] - outerPipeSegment2Length, outerPipeSegment1End[2]];
    const outerPipeSegment3End = [outerPipeSegment2End[0], outerPipeSegment2End[1], outerPipeSegment2End[2] - outerPipeSegment3Length];


    const innerPipeStart = [boxPosition[0] + innerOffset, boxPosition[1] + boxHeight / 2.4, boxPosition[2]];
    const innerPipeSegment1End = [innerPipeStart[0], innerPipeStart[1], innerPipeStart[2] + innerPipeSegment1Length];
    const innerPipeSegment2End = [innerPipeSegment1End[0], innerPipeSegment1End[1] - innerPipeSegment2Length, innerPipeSegment1End[2]];
    const innerPipeSegment3End = [innerPipeSegment2End[0], innerPipeSegment2End[1], innerPipeSegment2End[2] - innerPipeSegment3Length];


    const sprayPosition = [
        outerPipeSegment3End[0],
        outerPipeSegment3End[1] - 0.03,
        outerPipeSegment3End[2] + sprayLength / 2,
    ];


    const boxGeom = useMemo(() => buildPanel(boxLength, boxHeight, boxWidth), [boxLength, boxHeight, boxWidth]);
    const outerPipeGeom = useMemo(() => new THREE.CylinderGeometry(outerPipeRadius, outerPipeRadius, 1, 8), []);
    const innerPipeGeom = useMemo(() => new THREE.CylinderGeometry(innerPipeRadius, innerPipeRadius, 1, 8), []);

    return (
        <group>

            <mesh geometry={boxGeom} position={boxPosition} receiveShadow castShadow>
                <meshStandardMaterial color="Grey" />
                <Edges scale={1} renderOrder={1000}>
                    <meshBasicMaterial transparent color="#111" depthTest={true} />
                </Edges>
            </mesh>


            <mesh
                geometry={outerPipeGeom}
                position={[outerPipeStart[0], outerPipeStart[1], outerPipeStart[2] + outerPipeSegment1Length / 2]}
                scale={[1, outerPipeSegment1Length, 1]}
                rotation={[Math.PI / 2, 0, 0]}
                receiveShadow
                castShadow
            >
                <meshStandardMaterial color="Black" side={THREE.DoubleSide} />
                <Edges scale={1} renderOrder={1000}>
                    <meshBasicMaterial transparent color="Grey" depthTest={true} />
                </Edges>
            </mesh>


            <mesh
                geometry={outerPipeGeom}
                position={[outerPipeSegment1End[0], outerPipeSegment1End[1] - outerPipeSegment2Length / 2, outerPipeSegment1End[2]]}
                scale={[1, outerPipeSegment2Length, 1]}
                rotation={[0, 0, 0]}
                receiveShadow
                castShadow
            >
                <meshStandardMaterial color="Red" side={THREE.DoubleSide} />
                <Edges scale={1} renderOrder={1000}>
                    <meshBasicMaterial transparent color="Black" depthTest={true} />
                </Edges>
            </mesh>


            <mesh
                geometry={outerPipeGeom}
                position={[outerPipeSegment2End[0], outerPipeSegment2End[1], outerPipeSegment2End[2] - outerPipeSegment3Length / 2]}
                scale={[1, outerPipeSegment3Length, 1]}
                rotation={[Math.PI / 2, 0, 0]}
                receiveShadow
                castShadow
            >
                <meshStandardMaterial color="Black" side={THREE.DoubleSide} />
                <Edges scale={1} renderOrder={1000}>
                    <meshBasicMaterial transparent color="Grey" depthTest={true} />
                </Edges>
            </mesh>


            <mesh
                geometry={innerPipeGeom}
                position={[innerPipeStart[0], innerPipeStart[1], innerPipeStart[2] + innerPipeSegment1Length / 2]}
                scale={[1, innerPipeSegment1Length, 1]}
                rotation={[Math.PI / 2, 0, 0]}
                receiveShadow
                castShadow
            >
                <meshStandardMaterial color="DarkGrey" side={THREE.DoubleSide} />
                <Edges scale={1} renderOrder={1000}>
                    <meshBasicMaterial transparent color="Grey" depthTest={true} />
                </Edges>
            </mesh>


            <mesh
                geometry={innerPipeGeom}
                position={[innerPipeSegment1End[0], innerPipeSegment1End[1] - innerPipeSegment2Length / 2, innerPipeSegment1End[2]]}
                scale={[1, innerPipeSegment2Length, 1]}
                rotation={[0, 0, 0]}
                receiveShadow
                castShadow
            >
                <meshStandardMaterial color="Red" side={THREE.DoubleSide} />
                <Edges scale={1} renderOrder={1000}>
                    <meshBasicMaterial transparent color="Grey" depthTest={true} />
                </Edges>
            </mesh>


            <mesh
                geometry={innerPipeGeom}
                position={[innerPipeSegment2End[0], innerPipeSegment2End[1], innerPipeSegment2End[2] - innerPipeSegment3Length / 2]}
                scale={[1, innerPipeSegment3Length, 1]}
                rotation={[Math.PI / 2, 0, 0]}
                receiveShadow
                castShadow
            >
                <meshStandardMaterial color="DarkGrey" side={THREE.DoubleSide} />
                <Edges scale={1} renderOrder={1000}>
                    <meshBasicMaterial transparent color="Grey" depthTest={true} />
                </Edges>
            </mesh>


        </group>
    );
}